import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, CheckCircle2, ClipboardCheck, LayoutGrid, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { API, api } from '../api';
import { Logo } from '../components/Logo';

export default function PublicReview() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('all');
  const [busy, setBusy] = useState(false);
  const [photoSaved, setPhotoSaved] = useState(false);
  const camera = useRef(null);

  const savePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = `review-photo-${Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    setPhotoSaved(true);
    toast.success('Photo saved to your phone. Add it on Google after posting.');
    e.target.value = '';
  };

  const load = () => api.get(`/public/${slug}`).then((r) => setData(r.data));
  useEffect(() => {
    const early = window.__rgPublic;
    (early ? early.then((d) => (d ? setData(d) : load())) : load()).catch(() => setData(false));
  }, [slug]);

  const choose = (draft) => {
    if (busy) return;
    setBusy(true);
    Promise.resolve(navigator.clipboard?.writeText(draft.text)).catch(() => {
      toast.message('Copy it from here, then paste on Google', { description: draft.text });
    });
    const url = `${API}/public/${slug}/use/${draft.id}`;
    if (!navigator.sendBeacon?.(url)) {
      fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
    }
    window.location.href = data.google_url;
  };

  if (data === false) {
    return (
      <div className="public-page">
        <div className="center-box"><Logo size={44} /><h1>Link not found</h1><p>This review link may be mistyped or not set up yet.</p></div>
      </div>
    );
  }
  if (!data) return <div className="loading-screen">Loading…</div>;

  const { categories = [], featured = [] } = data;
  const drafts = tab === 'all'
    ? [...featured.map((id) => data.drafts.find((d) => d.id === id)).filter(Boolean),
       ...data.drafts.filter((d) => !featured.includes(d.id))]
    : data.drafts.filter((d) => d.category === tab);

  return (
    <div className="public-page">
      <div className="public-inner">
        {categories.length > 0 && (
          <div className="tab-strip" data-testid="public-tabs">
            <button className={`tab${tab === 'all' ? ' on' : ''}`} onClick={() => setTab('all')} data-testid="public-tab-all">
              <LayoutGrid size={16} /> All Reviews
            </button>
            {categories.map((c) => (
              <button key={c} className={`tab${tab === c ? ' on' : ''}`} onClick={() => setTab(c)} data-testid={`public-tab-${c}`}>
                <MessageSquare size={16} /> {c}
              </button>
            ))}
          </div>
        )}

        <div className="photo-strip">
          <button className={`photo-btn${photoSaved ? ' saved' : ''}`} onClick={() => camera.current.click()} data-testid="click-photo-button">
            <span className="photo-icon">{photoSaved ? <CheckCircle2 size={20} /> : <Camera size={20} />}</span>
            <span className="photo-copy">
              <strong>{photoSaved ? 'Photo saved on your phone' : 'Add a photo (optional)'}</strong>
              <span>{photoSaved ? 'Attach it with "Add photos" on Google' : 'Reviews with a photo get seen more'}</span>
            </span>
            <span className="photo-cta">{photoSaved ? 'Done' : 'Add Photo'}</span>
          </button>
          <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={savePhoto} data-testid="camera-input" />
        </div>

        {drafts.length === 0 ? (
          <div className="quote-card empty-card" data-testid="public-empty">
            <strong>No reviews here right now</strong>
            <p>Every ready-made review in this group has been used. Try another group above, or check back a little later.</p>
          </div>
        ) : (
          <div className="quote-list">
            {drafts.map((d, i) => (
              <article className="quote-card" key={d.id} data-testid={`public-draft-${i}`}>
                <p className="quote-text">{d.text}</p>
                <button className="copy-post" disabled={busy} onClick={() => choose(d)} data-testid={`public-copy-${i}`}>
                  <ClipboardCheck size={18} /> Copy &amp; Post on Google
                </button>
              </article>
            ))}
          </div>
        )}

        <p className="public-brand"><Logo size={20} /> Powered by Rankgate GMB</p>
      </div>
    </div>
  );
}
